import { useEffect, useState } from 'react';
import { Heart, Loader2 } from 'lucide-react';
import { useFavoriteIds, useToggleFavorite, type FavoriteTarget } from '../../hooks/useAPI';
import { useAuthStore } from '../../store/authStore';

type Variant = 'icon' | 'pill' | 'floating';

interface FavoriteButtonProps {
  target: FavoriteTarget;
  variant?: Variant;
  className?: string;
  /**
   * For screens that mount the card directly, we can skip the extra
   * useFavoriteIds query and hydrate from a provided set.
   */
  isFavouritedHint?: boolean;
}

/**
 * Heart toggle used on detail screens, list cards, and the home feed.
 *   - variant "icon" (default): small circular transparent button, fits
 *     inside existing headers.
 *   - variant "pill": outlined pill with "Save" / "Saved" label for
 *     action areas.
 *   - variant "floating": semi-opaque round badge overlaid on a card.
 *
 * If the user isn't signed in we show the button but tapping it prompts
 * the auth store's "require sign-in" behaviour via the onNavigate prop.
 * Here we just fail silently with the error in the toggle hook.
 */
export default function FavoriteButton({
  target, variant = 'icon', className = '', isFavouritedHint,
}: FavoriteButtonProps) {
  const userId = useAuthStore((s) => s.user?.id);
  const { data: ids, refetch: refetchIds } = useFavoriteIds();
  const { toggle, isLoading } = useToggleFavorite();

  // Track favourited state locally for immediate feedback, reseeded
  // whenever the underlying id set refreshes.
  const fromServer =
    typeof isFavouritedHint === 'boolean'
      ? isFavouritedHint
      : !!ids && ((ids as any)[target.kind] as string[] | undefined)?.includes(target.id);
  const [isFav, setIsFav] = useState(!!fromServer);
  useEffect(() => { setIsFav(!!fromServer); }, [fromServer]);

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!userId) {
      // Not signed in — nothing to do locally; upstream screens can prompt.
      return;
    }
    // Optimistic: flip the heart immediately, then reconcile on response.
    const next = !isFav;
    setIsFav(next);
    const { error } = await toggle(target, !next);
    if (error) setIsFav(!next); // revert
    (refetchIds as any)?.();
  };

  const iconSize =
    variant === 'pill' ? 'w-4 h-4' :
    variant === 'floating' ? 'w-4 h-4' : 'w-5 h-5';

  const label = isFav ? 'Saved' : 'Save';

  if (variant === 'pill') {
    return (
      <button onClick={onClick} disabled={isLoading} aria-pressed={isFav}
        className={`h-9 px-3 rounded-full text-xs flex items-center gap-1.5 transition-colors border ${
          isFav
            ? 'bg-[#D4F04A]/20 text-[#1A6B4F] border-[#1A6B4F]/20'
            : 'bg-card text-foreground border-border hover:border-[#1A6B4F]/40'
        } ${className}`}
      >
        {isLoading
          ? <Loader2 className={`${iconSize} animate-spin`} />
          : <Heart className={`${iconSize} ${isFav ? 'fill-[#1A6B4F]' : ''}`} />}
        {label}
      </button>
    );
  }

  if (variant === 'floating') {
    return (
      <button onClick={onClick} disabled={isLoading} aria-pressed={isFav}
        aria-label={label}
        className={`w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors disabled:opacity-60 ${className}`}
      >
        {isLoading
          ? <Loader2 className={`${iconSize} animate-spin`} />
          : <Heart className={`${iconSize} ${isFav ? 'fill-white text-white' : 'text-white'}`} />}
      </button>
    );
  }

  // default: icon
  return (
    <button onClick={onClick} disabled={isLoading} aria-pressed={isFav}
      aria-label={label}
      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-foreground/5 ${className}`}
    >
      {isLoading
        ? <Loader2 className={`${iconSize} animate-spin`} />
        : <Heart className={`${iconSize} ${isFav ? 'fill-[#1A6B4F] text-[#1A6B4F]' : 'text-muted-foreground'}`} />}
    </button>
  );
}
